<template>
  <div class="container pb-5">
    <div class="backResult my-3 font-weight-bold">
      <a href="#" @click.prevent="$router.go(-1)" style="text-decoration: underline" class="mr-2">
        <span class="material-icons icon-22pt">navigate_before</span>
      </a>
    </div>
    <div class="row">
      <div class="col-md-8">
        <product-image-carousel :product="product" height="520px" />
      </div>
      <div class="col-md-4">
        <h2 class="mb-2">{{ product.product_title }}</h2>
        <strong style="font-size: 17px;">{{ $n(product.price, 'currency', 'en-US') }}</strong>

        <div v-if="productColors.length" class="my-3">
          <div class="d-flex align-items-baseline mb-2">
            <strong style="font-size: 15px;">Color</strong>
            <span class="color-picker__value ml-2">{{ color ? getProductColorName(product, color) : 'Select a color' }}</span>
          </div>
          <div class="d-flex flex-wrap">
            <button
              v-for="productColor in productColors"
              :key="productColor"
              type="button"
              class="color-picker__swatch"
              :class="{ 'color-picker__swatch--selected': color === productColor }"
              :style="{ '--swatch-color': productColor }"
              :title="getProductColorName(product, productColor)"
              :aria-label="`Select color ${getProductColorName(product, productColor)}`"
              :aria-pressed="color === productColor ? 'true' : 'false'"
              @click.prevent="color = productColor"
            >
              <i
                v-if="color === productColor"
                class="material-icons color-picker__check"
                :style="{ color: getContrastColor(productColor) }"
                >check</i
              >
            </button>
          </div>
        </div>

        <div v-if="get(product, 'sizes')" class="d-flex w-100 align-items-center my-3">
          <strong class="mr-3" style="font-size: 15px;">
            Size
          </strong>
          <div class="custom-select-icon w-100" style="min-width: 50px">
            <b-form-select class="custom-select-icon__select" v-model="size" :options="sizeOptions"> ></b-form-select>
            <span class="material-icons custom-select-icon__icon">sort</span>
          </div>
        </div>

        <div class="d-flex align-items-center justify-content-between my-4">
          <div class="d-flex align-items-center">
            <a class="clickable-item" @click.prevent="decrementQuantity(product)">
              <i style="font-size: 20px;" :style="quantity === 1 ? 'color: lightgray' : ''" class="fas fa-minus"></i>
            </a>
            <div class="mx-3 d-flex align-items-center justify-content-center" style="font-size: 20px; width: 15px;">
              {{ $n(quantity) }}
            </div>
            <a class="clickable-item" @click.prevent="incrementQuantity(product)">
              <i style="font-size: 20px;" class="fas fa-plus"></i>
            </a>
          </div>

          <b-btn variant="primary" style="height: 40px; min-width: 240px;" @click.prevent="addCart">
            <span>Add To Cart</span>
          </b-btn>
        </div>

        <div class="mt-4">
          <h4 class="mb-2" style="text-transform: capitalize">Description</h4>
          <p>{{ product.product_description }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';
import { getContrastColor } from '@/common/utils';
import { getProductColorName } from '@/common/colorNames';
import { mapActions } from 'vuex';
import Page from '@/components/Page.vue';
import ProductImageCarousel from '@/components/ProductImageCarousel.vue';

export default {
  components: { ProductImageCarousel },
  extends: Page,
  data() {
    return {
      title: '',
      completeHeadTitle: '',
      quantity: 1,
      size: null,
      color: null,
      product: {},
      sizeOptions: [{ value: null, text: 'Please Select', disabled: true }],
      isLoading: false,
    };
  },
  computed: {
    productColors() {
      return get(this.product, 'colors.length') ? this.product.colors : this.product.color ? [this.product.color] : [];
    },

    isMobSmallScreen() {
      return this.windowWidth <= 420;
    },
  },

  watch: {
    product: {
      immediate: true,
      deep: true,
      handler(value) {
        if (get(value, 'id')) {
          this.completeHeadTitle = `${get(this.product, 'product_title')}`;
        }
      },
    },
  },

  methods: {
    get,
    getContrastColor,
    getProductColorName,
    ...mapActions('student', ['getPublicProduct']),
    ...mapActions('settings', ['setSettings']),
    ...mapActions('productsStore', ['addToCart']),

    addCart() {
      this.makeToast({ variant: 'success', msg: 'Product added successfully.' });
      this.addToCart({ product: this.product, quantity: this.quantity, size: this.size, color: this.color });
      this.quantity = 1;
    },

    incrementQuantity() {
      this.quantity += 1;
    },

    decrementQuantity() {
      if (this.quantity > 1) {
        this.quantity -= 1;
      }
    },

    async fetchProduct() {
      this.isLoading = true;

      const response = await this.getPublicProduct({
        slug: this.$route.params.slug,
      });

      this.product = response.data;
      this.sizeOptions = this.sizeOptions.concat(this.product.sizes);

      this.isLoading = false;
    },
  },

  async mounted() {
    if (this.$route.params.slug) {
      this.fetchProduct();
    }
    this.setSettings({ layout: { layout: 'fixed' } });
  },
};
</script>

<style scoped>
.color-picker__value {
  font-size: 13px;
  color: #868e96;
  letter-spacing: 0.02em;
}

.color-picker__swatch {
  position: relative;
  width: 34px;
  height: 34px;
  margin: 0 12px 12px 0;
  padding: 0;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 50%;
  background-color: var(--swatch-color);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.14);
}

.color-picker__swatch:hover {
  transform: translateY(-2px);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
}

.color-picker__swatch:focus {
  outline: none;
}

.color-picker__swatch--selected {
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--swatch-color), 0 1px 3px rgba(0, 0, 0, 0.14);
}

.color-picker__check {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 18px;
}
</style>
